/*
 * Based on react-data-sort and converted to hook.
 * https://github.com/Corjen/react-data-sort
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { matchSorter } from "match-sorter";

export const useDataSort = ({
  data,
  paginate,
  sortBy,
  direction,
  sortCompare,
  dataType,
  itemsPerPage,
  activePage,
  searchQuery = "",
  searchInColumns,
  onPagination
}) => {
  // {
  //   data,
  //   paginate = false,
  //   sortBy,
  //   direction = "asc",
  //   sortCompare,
  //   itemsPerPage = 10,
  //   activePage = 1,
  //   searchQuery,
  //   searchInColumns
  //   onPagenation
  // }
  const [state, setState] = useState({
    sortBy,
    dataType,
    direction: direction || "asc",
    sortCompare,
    pages: null,
    activePage: activePage || 1,
    searchQuery,
    rowsCount: data.length || 0
  });

  useEffect(() => {
    setState(prev => ({
      ...prev,
      searchQuery
    }));
  }, [searchQuery]);

  const reset = useCallback(() => {
    setState(prev => ({
      ...prev,
      dataType,
      sortBy,
      direction: "asc",
      sortCompare,
      activePage: activePage || 1
    }));
  }, [sortBy, dataType, sortCompare, activePage]);

  const goToPage = useCallback(
    activePage => {
      if (onPagination) {
        onPagination();
      }
      if (!paginate) {
        return;
      }
      if (
        typeof state.activePage !== "number" ||
        state.activePage < 0 ||
        state.activePage > state.pages
      ) {
        return;
      }
      setState(prev => ({ ...prev, activePage }));
    },
    [onPagination, paginate, state.activePage, state.pages]
  );

  const prevPage = useCallback(() => {
    if (!paginate) {
      return;
    }
    if (state.activePage === 0) {
      return;
    }
    goToPage(state.activePage - 1);
  }, [goToPage, paginate, state.activePage]);

  const nextPage = useCallback(() => {
    if (!paginate) {
      return;
    }
    if (state.activePage + 1 <= state.pages) {
      goToPage(state.activePage + 1);
    }
  }, [goToPage, paginate, state.activePage, state.pages]);

  const setDirection = useCallback(direction => {
    if (direction === "asc" || direction === "desc") {
      setState(prev => ({ ...prev, direction }));
    }
  }, []);

  const toggleDirection = useCallback(() => {
    setState(prev => ({
      ...prev,
      direction: prev.direction === "asc" ? "desc" : "asc"
    }));
  }, []);

  const setSortBy = useCallback(
    (key, dataType, sortCompare) => {
      if (
        (state.sortBy === key && !state.sortCompare) ||
        (state.sortCompare && state.sortCompare === sortCompare)
      ) {
        toggleDirection();
      }
      setState(prev => ({ ...prev, sortBy: key, dataType, sortCompare }));
    },
    [state.sortBy, state.sortCompare, toggleDirection]
  );

  const search = useCallback(value => {
    setState(prev => ({ ...prev, search: value }));
  }, []);

  // MATCH SORTER USAGE AVAIL AT https://www.npmjs.com/package/match-sorter#usage
  const searched = useMemo(() => {
    const keys = searchInColumns
      ? searchInColumns
      : data?.length
      ? Object.keys(data[0])
      : [];
    return state.searchQuery === ""
      ? data
      : matchSorter(data, state.searchQuery, {
          keys,
          threshold: matchSorter.rankings.CONTAINS,
          minRanking: matchSorter.rankings.CONTAINS
        });
  }, [data, searchInColumns, state.searchQuery]);

  const sorted = useMemo(() => {
    return state.sortBy === null && !state.sortCompare
      ? searched
      : sortData(
          searched,
          state.sortBy,
          state.direction,
          state.dataType,
          state.sortCompare
        );
  }, [
    searched,
    state.direction,
    state.sortBy,
    state.dataType,
    state.sortCompare
  ]);

  // if the data, itemsPerPage, or activePage
  useEffect(() => {
    const newCountOfRows = sorted.length;
    const newPageSize = calculatePages(newCountOfRows, itemsPerPage);

    if (
      data &&
      paginate &&
      (state?.pages !== newPageSize || state.rowsCount !== newCountOfRows)
    ) {
      setState(prev => ({
        ...prev,
        pages: newPageSize,
        activePage: prev.rowsCount !== newCountOfRows ? 1 : prev.activePage,
        rowsCount: newCountOfRows
      }));
    }
  }, [data, itemsPerPage, paginate, activePage, sorted, state]);

  return useMemo(
    () => ({
      data: paginate
        ? paginateData(sorted, state.activePage, itemsPerPage)
        : sorted,
      activePage: state.activePage,
      pages: state.pages,
      sortBy: state.sortBy,
      direction: state.direction,
      sortCompare: state.sortCompare,
      searchQuery: state.searchQuery,
      toggleDirection,
      reset,
      prevPage,
      nextPage,
      goToPage,
      setDirection,
      setSortBy,
      search,
      rowsCount: state.rowsCount
    }),
    [
      goToPage,
      nextPage,
      prevPage,
      itemsPerPage,
      paginate,
      reset,
      search,
      setDirection,
      setSortBy,
      sorted,
      state.activePage,
      state.direction,
      state.pages,
      state.searchQuery,
      state.sortBy,
      toggleDirection,
      state.sortCompare,
      state.rowsCount
    ]
  );
};

/*
 * Taken from https://github.com/Corjen/react-data-sort temporarily.
 * Need to either fork and open PR for changes or bundle this in our own package.
 */

export const calculatePages = (items, itemsPerPage) => {
  if (typeof items !== "number" || typeof itemsPerPage !== "number") {
    throw new Error("items and itemsPerPage should both be a number");
  }
  return Math.ceil(items / itemsPerPage);
};

export const paginateData = (data, activePage, itemsPerPage) => {
  if (typeof activePage !== "number" || typeof itemsPerPage !== "number") {
    throw new Error("activePage and itemsPerPage should both be a number");
  }

  // We use 0 to indicate we're viewing all of the data
  const VIEW_ALL_PAGE = 0;
  if (activePage === VIEW_ALL_PAGE) {
    return data;
  }

  // bootstrap pagination does not use 0-based indexing for page numbers
  // so we need to subtract 1 to correctly index into data
  const from = (activePage - 1) * itemsPerPage;
  const to = from + itemsPerPage;
  return data.slice(from, to);
};

// TODO/QUESTION: Should we default dataType to "string" ?
export const sortData = (
  data,
  key,
  direction = "asc",
  dataType = "string",
  sortCompare = null
) => {
  // Determine if we're reversing upfront so we only iterate array once
  // instead of sorting ascending and then reversing.
  const leastToGreatest = direction === "asc";
  const eq = 0;
  const lt = leastToGreatest ? -1 : 1;
  const gt = leastToGreatest ? 1 : -1;

  if (sortCompare) {
    return leastToGreatest
      ? [...data].sort(sortCompare)
      : [...data].sort(sortCompare).reverse();
  }
  if (dataType === "number") {
    return [...data].sort((a, b) =>
      parseFloat(a[key]) < parseFloat(b[key])
        ? lt
        : parseFloat(a[key]) > parseFloat(b[key])
        ? gt
        : eq
    );
  }
  if (dataType === "date") {
    return [...data].sort((a, b) => {
      const dateA = new Date(a[key]);
      const dateB = new Date(b[key]);

      // if the value doesn't seem to be a date then treat it like a string
      if (isNaN(dateA.valueOf()) || isNaN(dateB.valueOf())) {
        return a[key] < b[key] ? lt : a[key] > b[key] ? gt : eq;
      }

      return dateA.valueOf() < dateB.valueOf()
        ? lt
        : dateA.valueOf() > dateB.valueOf()
        ? gt
        : eq;
    });
  }
  // else, sort by string
  return [...data].sort((a, b) =>
    a[key] < b[key] ? lt : a[key] > b[key] ? gt : eq
  );
};
